import { useState } from "react";
import {
  Stack,
  HStack,
  Box,
  VStack,
  Text,
  Collapse,
  BoxProps,
  Icon,
} from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { DragControls } from "framer-motion";
import { FiX } from "react-icons/fi";
export interface SectionProps extends BoxProps {
  title?: string;
  info?: string;
  description?: string;
  defaultExpanded?: boolean;
  width?: string | number;
  children: React.ReactNode;
  isDragable?: boolean;
  dragControls?: DragControls;
  isDisabled?: boolean;
  onRemove?: () => void;
  handleDragEnd?: () => void;
}

export const Header = ({
  title,
  info,
  isDragable,
  children,
  onPointerDown,
}: {
  title: string;
  info?: string;
  isDragable?: boolean;
  children?: React.ReactNode;
  onPointerDown?: (e: React.PointerEvent) => void;
}) => (
  <HStack
    w="full"
    align="center"
    spacing={2}
    onPointerDown={onPointerDown}
    cursor="grab"
    _active={{ cursor: "grabbing" }}
    draggable={false}
  >
    {isDragable && (
      <Box
        w="24px"
        h="24px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <img src="/icons/bars.svg" alt="drag-handle" />
      </Box>
    )}
    <Text fontWeight="600" fontSize="20px" m={0}>
      {title}
    </Text>
    {children}
    {info && (
      <Text color="#1A202C80" fontWeight="400" fontSize="16px">
        {info}
      </Text>
    )}
  </HStack>
);

export const Description = ({
  description,
  variant = "default",
}: {
  description: string;
  variant?: "default" | "highlight";
}) => (
  <Text
    w="full"
    fontFamily="Helvetica"
    fontSize={variant === "default" ? "12px" : "14px"}
    fontWeight="400"
    color={variant === "default" ? "#666666" : "#000000CC"}
  >
    {description}
  </Text>
);

const Section = ({
  title,
  info,
  description,
  defaultExpanded = true,
  width = "100%",
  children,
  isDragable = true,
  dragControls,
  isDisabled,
  onRemove,
  onPointerDown = () => {},
  ...props
}: SectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Box
      position="relative"
      width={typeof width === "string" ? width : `${width}px`}
      borderBottom="1px solid"
      borderColor="#00000033"
      pb={6}
      mt={6}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      {...props}
    >
      <Stack w="100%">
        {title && (
          <HStack
            justifyContent="space-between"
            w="100%"
            cursor={isDragable ? "pointer" : "default"}
          >
            <Header
              title={title}
              info={info}
              isDragable={isDragable}
              onPointerDown={onPointerDown}
            >
              {onRemove && !isDisabled && (
                <HStack
                  ml={"auto"}
                  px={2}
                  color="blue"
                  as="button"
                  spacing={1}
                  onClick={() => onRemove()}
                  cursor="pointer"
                >
                  <Icon as={FiX} fontSize="12px" />
                  <Text fontSize="12px">Remove block</Text>
                </HStack>
              )}
            </Header>

            <Box
              as="button"
              onClick={() => setIsExpanded((v) => !v)}
              cursor="pointer"
            >
              {!isExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </Box>
          </HStack>
        )}

        <Collapse
          in={isExpanded}
          animateOpacity
          style={{ overflow: "visible" }}
        >
          <VStack spacing="10px" alignItems="flex-start">
            {children}
          </VStack>
        </Collapse>
      </Stack>
    </Box>
  );
};

export default Section;
